import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FaCamera, FaFolderOpen, FaTimes, FaSpinner, FaExclamationTriangle,
  FaCheckCircle, FaEye, FaShoePrints, FaLayerGroup, FaArrowRight,
} from 'react-icons/fa';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import CameraCapture from '../components/CameraCapture';
import DemographicsFields from '../components/DemographicsFields';
import ClinicalContextFields from '../components/ClinicalContextFields';
import AssistantPanel from '../components/AssistantPanel';
import { analyzeEncounter, getEncounterSites, ensureToken } from '../utils/api';

/**
 * Multi-image screening encounter.
 *
 * This page exists because the rest of the product assumed one image at a time,
 * and that is not how screening happens. At a diabetes clinic a community
 * health worker photographs **both eyes and both feet** in one visit and types
 * what the patient told them. Four separate single-image analyses throw away
 * the three things that matter most:
 *
 *   - bilateral asymmetry (DR in one eye vs both means different things;
 *     a unilateral foot ulcer suggests trauma, bilateral suggests neuropathy)
 *   - corroboration across organs (retinopathy AND a foot ulcer in a
 *     long-standing diabetic is one systemic picture, not two referrals)
 *   - the typed context, which no image classifier can know
 *
 * The backend reasons over the whole set and returns one triage priority for
 * the visit.
 */

// A sensible default for the lead use case, so the common path is one tap.
const DIABETES_PRESET = ['right_eye', 'left_eye', 'right_foot', 'left_foot'];

const PRIORITY_STYLES = {
  urgent: {
    wrap: 'bg-red-950/60 border-red-700',
    text: 'text-red-300',
    badge: 'bg-red-600',
    label: 'Urgent',
  },
  priority: {
    wrap: 'bg-amber-950/60 border-amber-700',
    text: 'text-amber-300',
    badge: 'bg-amber-600',
    label: 'Priority',
  },
  routine: {
    wrap: 'bg-emerald-950/60 border-emerald-800',
    text: 'text-emerald-300',
    badge: 'bg-emerald-700',
    label: 'Routine',
  },
  unknown: {
    wrap: 'bg-gray-800 border-gray-700',
    text: 'text-gray-300',
    badge: 'bg-gray-600',
    label: 'Inconclusive',
  },
};

const RISK_COLOR = {
  malignant: 'text-red-400',
  critical: 'text-red-400',
  severe: 'text-red-400',
  high: 'text-orange-400',
  'pre-malignant': 'text-orange-400',
  moderate: 'text-amber-400',
  benign: 'text-emerald-400',
  low: 'text-emerald-400',
  normal: 'text-emerald-400',
};

const siteIcon = (site) =>
  site.includes('eye') ? <FaEye /> : site.includes('foot') ? <FaShoePrints /> : <FaLayerGroup />;

const EncounterAnalysis = () => {
  const [sites, setSites] = useState([]);
  const [maxImages, setMaxImages] = useState(10);
  const [sitesError, setSitesError] = useState(null);

  // Each study: { id, file, site, previewUrl, source: 'camera' | 'file' }
  const [studies, setStudies] = useState([]);
  const [context, setContext] = useState({});
  const [cameraFor, setCameraFor] = useState(null); // site key or null
  const [pendingSite, setPendingSite] = useState('right_eye');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const fileInputRef = useRef(null);
  // Revoking object URLs needs the CURRENT list at unmount, but we must not
  // re-run cleanup on every add/remove (that would revoke URLs still on screen).
  // A ref sidesteps the stale-closure problem without adding studies as a dep.
  const studiesRef = useRef(studies);
  studiesRef.current = studies;

  useEffect(() => {
    getEncounterSites()
      .then((d) => {
        setSites(d.sites.filter((s) => s.modality));
        setMaxImages(d.max_images);
      })
      .catch((e) => setSitesError(e.message || 'Could not load site list'));
    return () => studiesRef.current.forEach((s) => URL.revokeObjectURL(s.previewUrl));
  }, []);

  const siteLabel = useCallback(
    (key) => sites.find((s) => s.key === key)?.label || key,
    [sites]
  );

  const addFiles = useCallback(
    (fileList, site) => {
      const incoming = Array.from(fileList || []);
      if (!incoming.length) return;
      setError(null);
      setStudies((prev) => {
        const room = maxImages - prev.length;
        if (room <= 0) {
          setError(`Maximum ${maxImages} images per encounter.`);
          return prev;
        }
        const added = incoming.slice(0, room).map((file, i) => ({
          id: `${Date.now()}-${i}-${Math.random().toString(36).slice(2, 8)}`,
          file,
          site: site || 'other',
          previewUrl: URL.createObjectURL(file),
          source: site ? 'camera' : 'file',
        }));
        if (incoming.length > room) {
          setError(`Only ${room} more image${room === 1 ? '' : 's'} fit — ${incoming.length - room} skipped.`);
        }
        return [...prev, ...added];
      });
    },
    [maxImages]
  );

  const removeStudy = (id) =>
    setStudies((prev) => {
      const gone = prev.find((s) => s.id === id);
      if (gone) URL.revokeObjectURL(gone.previewUrl);
      return prev.filter((s) => s.id !== id);
    });

  const setStudySite = (id, site) =>
    setStudies((prev) => prev.map((s) => (s.id === id ? { ...s, site } : s)));

  const untagged = useMemo(
    () => studies.filter((s) => !s.site || s.site === 'other'),
    [studies]
  );

  const submit = async () => {
    if (!studies.length) {
      setError('Add at least one image.');
      return;
    }
    if (untagged.length) {
      setError(
        `Tag every image with an anatomical site — ${untagged.length} still untagged. ` +
          'The site determines which model reads it.'
      );
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const token = await ensureToken(localStorage.getItem('token'));
      const payload = studies.map(({ file, site }) => ({ file, site }));
      setResult(await analyzeEncounter(payload, token, context));
    } catch (e) {
      setError(e.message || 'Encounter analysis failed.');
    } finally {
      setLoading(false);
    }
  };

  const applyPreset = () => {
    setError(null);
    setPendingSite(DIABETES_PRESET[Math.min(studies.length, 3)]);
  };

  // The assistant's opening lines, built from the encounter as a whole. The
  // backend's per-image `assistant` blocks each describe one image; a clinician
  // reading an encounter needs the visit-level picture.
  const encounterTopFinding = useMemo(() => {
    const positives = (result?.studies || [])
      .filter((st) => st.result && st.result.top_finding)
      .map((st) => `${st.site_label}: ${st.result.top_finding}`);
    return positives.join('; ') || 'No positive findings';
  }, [result]);

  const encounterAssistant = useMemo(() => {
    if (!result) return undefined;
    const syn = result.synthesis;
    return {
      clinician:
        syn?.narrative ||
        `${result.analysed_count} image(s) analysed across this visit. Triage: ` +
          `${result.triage?.priority}. ${result.triage?.reason || ''}`,
      patient:
        syn?.patient_explanation ||
        'Your screening is complete. A clinician will go through the results with you.',
    };
  }, [result]);

  const priority = result?.triage?.priority || 'unknown';
  const ps = PRIORITY_STYLES[priority] || PRIORITY_STYLES.unknown;

  return (
    <div className="min-h-screen bg-gray-950 text-gray-200 flex flex-col">
      <Navbar />

      <main className="flex-1 max-w-5xl w-full mx-auto px-4 py-10">
        <header className="mb-8">
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <FaLayerGroup className="text-purple-400" />
            Screening encounter
          </h1>
          <p className="mt-2 text-sm text-gray-400 max-w-2xl">
            Capture several images from one patient visit — both eyes and both
            feet in a diabetes screen, for example — and have them read together
            rather than separately. Asymmetry between sides, findings that
            corroborate across organs, and the context you type are all used to
            produce one triage decision for the visit.
          </p>
        </header>

        {sitesError && (
          <div className="mb-6 rounded-lg border border-red-800 bg-red-950/60 p-4 text-sm text-red-300">
            Could not reach the backend to load the site list: {sitesError}
          </div>
        )}

        {/* ---------- capture ---------- */}
        <section className="mb-6 rounded-xl border border-gray-800 bg-gray-900/60 p-5">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Capture site</label>
              <select
                value={pendingSite}
                onChange={(e) => setPendingSite(e.target.value)}
                className="bg-gray-900 border border-gray-700 rounded-md px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-purple-500"
              >
                {sites.map((s) => (
                  <option key={s.key} value={s.key}>
                    {s.label} — {s.modality}
                  </option>
                ))}
              </select>
            </div>

            <button
              onClick={() => setCameraFor(pendingSite)}
              disabled={studies.length >= maxImages}
              className="px-4 py-2 text-sm rounded-md bg-purple-600 hover:bg-purple-500 disabled:opacity-40 text-white flex items-center gap-2"
            >
              <FaCamera /> Use camera
            </button>

            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={studies.length >= maxImages}
              className="px-4 py-2 text-sm rounded-md border border-gray-600 text-gray-200 hover:bg-gray-800 disabled:opacity-40 flex items-center gap-2"
            >
              <FaFolderOpen /> Choose files
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={(e) => {
                addFiles(e.target.files, pendingSite);
                e.target.value = ''; // so re-picking the same file fires onChange
              }}
            />

            <button
              onClick={applyPreset}
              className="px-3 py-2 text-xs rounded-md border border-purple-800 text-purple-300 hover:bg-purple-950/50"
              title="Both eyes and both feet — the standard diabetes screen"
            >
              Diabetes screen (2 eyes + 2 feet)
            </button>

            <span className="ml-auto text-xs text-gray-500">
              {studies.length} / {maxImages} images
            </span>
          </div>
          <p className="mt-3 text-[11px] text-gray-600">
            Files picked from disk are tagged with the selected site; you can
            change any tag below. The camera opens on the rear lens where
            available.
          </p>
        </section>

        {/* ---------- staged images ---------- */}
        {studies.length > 0 && (
          <section className="mb-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {studies.map((s) => (
              <div
                key={s.id}
                className="relative rounded-lg border border-gray-800 bg-gray-900 overflow-hidden"
              >
                <button
                  onClick={() => removeStudy(s.id)}
                  aria-label="Remove image"
                  className="absolute top-1.5 right-1.5 z-10 bg-black/70 hover:bg-red-700 text-white rounded-full p-1.5"
                >
                  <FaTimes className="text-xs" />
                </button>
                <img
                  src={s.previewUrl}
                  alt={siteLabel(s.site)}
                  className="w-full h-28 object-cover"
                />
                <div className="p-2">
                  <select
                    value={s.site}
                    onChange={(e) => setStudySite(s.id, e.target.value)}
                    className={`w-full bg-gray-950 border rounded px-2 py-1 text-xs text-gray-200 focus:outline-none ${
                      !s.site || s.site === 'other'
                        ? 'border-amber-600'
                        : 'border-gray-700 focus:border-purple-500'
                    }`}
                  >
                    <option value="other">— tag the site —</option>
                    {sites.map((o) => (
                      <option key={o.key} value={o.key}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-[10px] text-gray-600 truncate">
                    {s.source === 'camera' ? 'Camera capture' : s.file.name}
                  </p>
                </div>
              </div>
            ))}
          </section>
        )}

        {/* ---------- context ---------- */}
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <DemographicsFields
            value={context}
            onChange={setContext}
            showDiabetesFields
          />
          <ClinicalContextFields value={context} onChange={setContext} />
        </div>

        {error && (
          <div className="mb-6 rounded-lg border border-red-800 bg-red-950/60 p-4 text-sm text-red-300 flex items-start gap-3">
            <FaExclamationTriangle className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <button
          onClick={submit}
          disabled={loading || !studies.length}
          className="w-full py-3 rounded-lg bg-purple-600 hover:bg-purple-500 disabled:opacity-40 text-white font-medium flex items-center justify-center gap-3"
        >
          {loading ? (
            <>
              <FaSpinner className="animate-spin" />
              Analysing {studies.length} image{studies.length === 1 ? '' : 's'}…
            </>
          ) : (
            <>
              Analyse encounter <FaArrowRight />
            </>
          )}
        </button>
        {loading && (
          <p className="mt-2 text-center text-xs text-gray-500">
            Each image runs through its own model, then the set is reasoned over
            together. Allow up to a minute.
          </p>
        )}

        {/* ---------- results ---------- */}
        {result && (
          <div className="mt-10 space-y-6">
            <div className={`rounded-xl border p-5 ${ps.wrap}`}>
              <div className="flex items-center gap-3 flex-wrap">
                <span
                  className={`${ps.badge} text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide`}
                >
                  {ps.label}
                </span>
                <span className="text-xs text-gray-400">
                  {result.analysed_count} of {result.image_count} images analysed
                  {result.failed_count > 0 && ` · ${result.failed_count} failed`}
                </span>
              </div>
              <p className={`mt-3 text-sm ${ps.text}`}>{result.triage.reason}</p>
            </div>

            {/* per image */}
            <section>
              <h2 className="text-sm font-semibold text-gray-300 mb-3">Per-image findings</h2>
              <div className="space-y-2">
                {result.studies.map((st) => {
                  const r = st.result;
                  return (
                    <div
                      key={st.index}
                      className="rounded-lg border border-gray-800 bg-gray-900/60 p-4 flex items-start gap-4"
                    >
                      <span className="text-purple-400 mt-0.5">{siteIcon(st.site)}</span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <span className="text-sm font-medium text-gray-200">
                            {st.site_label}
                          </span>
                          <span className="text-[11px] text-gray-600">{st.modality}</span>
                        </div>
                        {r ? (
                          <>
                            <p className="mt-1 text-sm">
                              <span className={RISK_COLOR[(r.risk || '').toLowerCase()] || 'text-gray-300'}>
                                {r.top_finding}
                              </span>
                              <span className="text-gray-500">
                                {' '}· {Math.round((r.predictions?.[0]?.confidence || 0) * 100)}% ·
                                risk {r.risk}
                              </span>
                            </p>
                            {r.uncertainty?.uncertain && (
                              <p className="mt-1 text-[11px] text-amber-400">
                                Model flagged this prediction as unstable — consider retaking.
                              </p>
                            )}
                            {r.provider?.fallback_used && (
                              <p className="mt-1 text-[11px] text-amber-400">
                                {r.provider.note}
                              </p>
                            )}
                          </>
                        ) : (
                          <p className="mt-1 text-sm text-red-400">
                            Not analysed — {st.error}
                          </p>
                        )}
                      </div>
                      {st.image_url && (
                        <img
                          src={`${process.env.REACT_APP_BACKEND_URL || ''}${st.image_url}`}
                          alt=""
                          className="w-14 h-14 rounded object-cover border border-gray-800 shrink-0"
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </section>

            {result.bilateral_comparison?.length > 0 && (
              <section>
                <h2 className="text-sm font-semibold text-gray-300 mb-3">
                  Left vs right comparison
                </h2>
                <div className="space-y-2">
                  {result.bilateral_comparison.map((b, i) => (
                    <div
                      key={i}
                      className="rounded-lg border border-gray-800 bg-gray-900/60 p-4"
                    >
                      <p className="text-sm text-gray-200">{b.summary}</p>
                      {b.clinical_note && (
                        <p className="mt-1 text-xs text-gray-500">{b.clinical_note}</p>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {result.cross_findings?.length > 0 && (
              <section>
                <h2 className="text-sm font-semibold text-gray-300 mb-3">
                  Patterns across organs
                </h2>
                <div className="space-y-2">
                  {result.cross_findings.map((c, i) => (
                    <div
                      key={i}
                      className="rounded-lg border border-amber-900/70 bg-amber-950/30 p-4"
                    >
                      <p className="text-sm text-amber-200">{c.summary}</p>
                      <p className="mt-2 text-xs text-gray-300">
                        <strong className="text-gray-200">Action:</strong> {c.action}
                      </p>
                      <p className="mt-1 text-[11px] text-gray-600">
                        Based on: {c.evidence.join(', ')}
                      </p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {result.synthesis ? (
              <section className="rounded-xl border border-purple-900/60 bg-purple-950/20 p-5">
                <h2 className="text-sm font-semibold text-purple-200 mb-3">
                  Encounter summary
                </h2>
                <p className="text-sm text-gray-200 leading-relaxed">
                  {result.synthesis.narrative}
                </p>
                {result.synthesis.context_changed_interpretation && (
                  <p className="mt-3 text-sm text-purple-200">
                    <strong>What the typed context changed:</strong>{' '}
                    {result.synthesis.context_changed_interpretation}
                  </p>
                )}
                {result.synthesis.next_steps?.length > 0 && (
                  <ol className="mt-3 space-y-1 text-sm text-gray-300 list-decimal list-inside">
                    {result.synthesis.next_steps.map((n, i) => (
                      <li key={i}>{n}</li>
                    ))}
                  </ol>
                )}
                {result.synthesis.patient_explanation && (
                  <p className="mt-4 pt-3 border-t border-purple-900/60 text-sm text-gray-400">
                    <strong className="text-gray-300">For the patient:</strong>{' '}
                    {result.synthesis.patient_explanation}
                  </p>
                )}
              </section>
            ) : (
              <section className="rounded-xl border border-gray-800 bg-gray-900/60 p-5">
                <h2 className="text-sm font-semibold text-gray-300 mb-2">
                  Encounter summary unavailable
                </h2>
                <p className="text-xs text-gray-500">
                  The narrative summary needs a hosted language model and none is
                  reachable right now. The comparison, cross-organ patterns and
                  triage above are computed locally and are unaffected.
                </p>
              </section>
            )}

            {/* The honesty block. Rendered always, including when no text was
                typed, so the distinction is visible rather than discovered. */}
            <section className="rounded-lg border border-gray-800 bg-gray-900/40 p-4">
              <h3 className="text-xs font-semibold text-gray-400 mb-2">
                How your typed context was used
              </h3>
              <ul className="space-y-1 text-[11px] text-gray-500">
                <li className="flex items-center gap-2">
                  {result.context_influence.clinical_text_supplied ? (
                    <FaCheckCircle className="text-emerald-500" />
                  ) : (
                    <FaTimes className="text-gray-600" />
                  )}
                  Clinical text supplied
                </li>
                <li className="flex items-center gap-2">
                  {result.context_influence.entered_model_prompt_for?.length ? (
                    <FaCheckCircle className="text-emerald-500" />
                  ) : (
                    <FaTimes className="text-gray-600" />
                  )}
                  Entered the model prompt for:{' '}
                  {result.context_influence.entered_model_prompt_for?.join(', ') || 'none'}
                </li>
                <li className="flex items-center gap-2">
                  {result.context_influence.informed_deterministic_rules ? (
                    <FaCheckCircle className="text-emerald-500" />
                  ) : (
                    <FaTimes className="text-gray-600" />
                  )}
                  Informed the cross-organ rules
                </li>
                <li className="flex items-center gap-2">
                  {result.context_influence.informed_synthesis ? (
                    <FaCheckCircle className="text-emerald-500" />
                  ) : (
                    <FaTimes className="text-gray-600" />
                  )}
                  Informed the encounter summary
                </li>
              </ul>
              <p className="mt-3 text-[11px] text-gray-600 leading-relaxed">
                {result.context_influence.note}
              </p>
            </section>

            {/* Zafya AI follow-up. Grounded in the encounter as a whole —
                triage decision, the per-site findings and the cross-organ
                patterns — rather than a single image, so a clinician can ask
                "why priority and not urgent?" and get an answer tied to this
                visit. */}
            <AssistantPanel
              analysisId={result.id}
              assistant={encounterAssistant}
              assistantName="Zafya AI"
              disclaimer={result.disclaimer}
              severity={result.triage?.priority === 'urgent' ? 'High' : undefined}
              risk={result.triage?.highest_risk}
              chatContext={{
                modality: 'encounter',
                top_finding: encounterTopFinding,
                severity: result.triage?.priority,
                triage_reason: result.triage?.reason,
                sites_analysed: result.studies
                  .filter((st) => st.result)
                  .map((st) => `${st.site_label}: ${st.result.top_finding}`)
                  .join('; '),
                bilateral: result.bilateral_comparison?.map((b) => b.summary).join(' '),
                cross_findings: result.cross_findings?.map((c) => c.summary).join(' '),
                age: context.age,
                sex: context.sex,
              }}
            />

            <p className="text-[11px] text-gray-600 text-center">{result.disclaimer}</p>
          </div>
        )}
      </main>

      <CameraCapture
        open={!!cameraFor}
        label={cameraFor ? siteLabel(cameraFor) : ''}
        onClose={() => setCameraFor(null)}
        onCapture={(file) => addFiles([file], cameraFor)}
      />

      <Footer />
    </div>
  );
};

export default EncounterAnalysis;
